import { useState, useEffect, useRef } from "react";
import { getAuthHeaders } from "../credits";

const PENDING_PAYMENT_KEY = "ghostdetect_pending_payment";

interface PaywallModalProps {
	onSuccess: () => void;
}

export function PaywallModal({ onSuccess }: PaywallModalProps) {
	const [status, setStatus] = useState<"idle" | "loading" | "verifying" | "error">("idle");
	const [errorMsg, setErrorMsg] = useState("");
	const paymentIdRef = useRef<string | null>(null);
	const initializedRef = useRef(false);

	useEffect(() => {
		if (initializedRef.current) return;
		initializedRef.current = true;

		import("dodopayments-checkout").then(({ DodoPayments }) => {
			DodoPayments.Initialize({
				mode: process.env.NODE_ENV === "production" ? "live" : "test",
				displayType: "overlay",
				onEvent: async (event: any) => {
					const pid = paymentIdRef.current;
					if (!pid) return;

					if (event.type === "checkout.closed" || event.type === "checkout.redirect") {
						await verifyAndCredit(pid);
					}
					if (event.type === "checkout.error") {
						setStatus("error");
						setErrorMsg("Payment failed. Please try again.");
					}
				},
			});
		});
	}, []);

	async function verifyAndCredit(pid: string) {
		setStatus("verifying");
		const headers = await getAuthHeaders();

		// In live mode, payment may still be processing when the overlay closes.
		// Poll a few times before giving up.
		const MAX_ATTEMPTS = 6;
		const DELAY_MS = 3000;

		for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
			try {
				const res = await fetch("/api/verify-payment", {
					method: "POST",
					headers,
					body: JSON.stringify({ paymentId: pid }),
				});
				const data = await res.json();
				if (data.verified) {
					localStorage.removeItem(PENDING_PAYMENT_KEY);
					onSuccess();
					return;
				}
				// If not verified yet (still processing), wait and retry
			} catch {
				// Network error — wait and retry
			}
			if (attempt < MAX_ATTEMPTS - 1) {
				await new Promise((r) => setTimeout(r, DELAY_MS));
			}
		}

		setStatus("error");
		setErrorMsg("Payment not confirmed yet. Please refresh the page to check again.");
	}

	async function handlePurchase() {
		setStatus("loading");
		setErrorMsg("");

		try {
			const headers = await getAuthHeaders();
			const res = await fetch("/api/create-checkout", {
				method: "POST",
				headers,
				body: JSON.stringify({}),
			});
			const data = await res.json();

			if (!data.paymentLink) {
				throw new Error("No payment link returned");
			}

			// Store in ref (for overlay events) and localStorage (survives redirect)
			paymentIdRef.current = data.paymentId;
			localStorage.setItem(PENDING_PAYMENT_KEY, data.paymentId);

			const { DodoPayments } = await import("dodopayments-checkout");
			DodoPayments.Checkout.open({ checkoutUrl: data.paymentLink });
			setStatus("idle");
		} catch {
			setStatus("error");
			setErrorMsg("Failed to start checkout. Please try again.");
		}
	}

	return (
		<div className="paywall-backdrop" style={{ paddingBottom: "var(--safe-bottom)" }}>
			<div className="paywall-card animate-scale-in">
				{/* Ghost icon */}
				<div className="text-4xl mb-4 select-none" aria-hidden>
					👻
				</div>

				<h2
					style={{ fontFamily: "var(--font-display)", lineHeight: 1.1 }}
					className="text-2xl sm:text-3xl text-white mb-2"
				>
					You're out of free checks
				</h2>

				<p
					className="text-xs sm:text-sm mb-6 max-w-xs mx-auto"
					style={{ color: "var(--text-secondary)" }}
				>
					Get 5 more analyses to keep investigating suspicious job postings.
				</p>

				{/* Pricing */}
				<div
					className="rounded-xl px-5 py-4 mb-6"
					style={{
						background: "rgba(255,255,255,0.03)",
						border: "1px solid var(--border)",
					}}
				>
					<div className="flex items-baseline justify-center gap-1.5">
						<span
							className="text-3xl font-semibold"
							style={{ color: "var(--text-primary)" }}
						>
							₹25
						</span>
						<span
							className="text-sm"
							style={{ color: "var(--text-tertiary)" }}
						>
							for 5 credits
						</span>
					</div>
					<p
						className="text-center mt-1 mono-label"
						style={{ color: "var(--text-tertiary)" }}
					>
						₹5 per analysis · +GST
					</p>
				</div>

				{/* CTA */}
				<button
					onClick={handlePurchase}
					disabled={status === "loading" || status === "verifying"}
					className="w-full py-3.5 rounded-xl font-semibold text-sm tracking-wide transition-all duration-300 active:scale-[0.98]"
					style={{
						background: status === "loading" || status === "verifying" ? "rgba(255,255,255,0.08)" : "#fff",
						color: status === "loading" || status === "verifying" ? "rgba(255,255,255,0.2)" : "#08080a",
						cursor: status === "loading" || status === "verifying" ? "not-allowed" : "pointer",
						boxShadow: status === "loading" || status === "verifying" ? "none" : "0 2px 16px rgba(255,255,255,0.1)",
					}}
				>
					{status === "loading" ? "Opening checkout…" : status === "verifying" ? "Verifying payment…" : "Buy 5 Credits →"}
				</button>

				{errorMsg && (
					<p
						className="text-xs mt-3 animate-fade-in"
						style={{ color: "var(--ghost-red)" }}
					>
						{errorMsg}
					</p>
				)}

				<p
					className="text-center mt-4 mono-label"
					style={{ color: "var(--text-ghost)" }}
				>
					Secure payment via Dodo Payments
				</p>
			</div>
		</div>
	);
}

/** Check for a pending payment on page load (after redirect from Dodo) */
export async function checkPendingPayment(): Promise<boolean> {
	const pid = localStorage.getItem(PENDING_PAYMENT_KEY);
	if (!pid) return false;

	const headers = await getAuthHeaders();
	const MAX_ATTEMPTS = 6;
	const DELAY_MS = 3000;

	for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
		try {
			const res = await fetch("/api/verify-payment", {
				method: "POST",
				headers,
				body: JSON.stringify({ paymentId: pid }),
			});
			const data = await res.json();
			if (data.verified) {
				localStorage.removeItem(PENDING_PAYMENT_KEY);
				return true;
			}
		} catch {
			// Network error — retry
		}
		if (attempt < MAX_ATTEMPTS - 1) {
			await new Promise((r) => setTimeout(r, DELAY_MS));
		}
	}
	return false;
}
